import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import styles from "../styles/login.module.css";
import {
  googleAuth,
  registerUser,
  loginUser,
  forgotPasswordRequest,
} from "../utils/api";
import { useAuth } from "../context/AuthContext";
import "@fortawesome/fontawesome-free/css/all.min.css";
import promoImage from "../assets/login-promo-image.png";
import CustomModal from "../components/CustomModal";

const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login: contextLogin,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth();

  const [isLoginView, setIsLoginView] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    const from = location.state?.from?.pathname || "/dashboard";
    if (!isAuthLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, navigate, location.state]);

  const handleApiError = (err) => {
    const message =
      err.response?.data?.error ||
      err.message ||
      "An unexpected error occurred.";
    setError(message);
  };

  const handleGoogleSuccess = async (response) => {
    setIsProcessing(true);
    setError(null);
    try {
      const res = await googleAuth(response.credential);
      if (!res.data.success) throw new Error(res.data.error);
      contextLogin(res.data.user);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    try {
      if (isLoginView) {
        const res = await loginUser({ email, password });
        if (!res.data.success) throw new Error(res.data.error);
        contextLogin(res.data.user);
      } else {
        const res = await registerUser({ name, email, password });
        if (!res.data.success) throw new Error(res.data.error);
        setModalMessage("Registration successful! Please log in.");
        setIsLoginView(true);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForgotPassword = async () => {
    const userEmail = prompt("Please enter your email to reset password:");
    if (!userEmail || !userEmail.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const res = await forgotPasswordRequest(userEmail);
      setModalMessage(
        res.data.message || "If an account exists, instructions were sent."
      );
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleView = () => {
    setIsLoginView(!isLoginView);
    setError(null);
    setName("");
    setEmail("");
    setPassword("");
  };

  if (isAuthLoading) {
    return (
      <div className={styles.fullPageLoader}>
        <div className={styles.spinner}></div>
        <p>Initializing Session...</p>
      </div>
    );
  }
  if (isAuthenticated) return null;

  return (
    <div className={styles.loginPage}>
      <Link to="/" className={styles.homeLink}>
        <i className="fas fa-home"></i>
      </Link>
      {modalMessage && (
        <CustomModal
          message={modalMessage}
          onConfirm={() => setModalMessage(null)}
        />
      )}

      <div className={styles.loginContainer}>
        <div className={styles.formSide}>
          <div className={styles.formContent}>
            <h1 className={styles.title}>
              {isLoginView ? "LOGIN" : "SIGN UP"}
            </h1>
            <p className={styles.subtitle}>
              {isLoginView
                ? "Welcome back! Please enter your details."
                : "Create an account to get started."}
            </p>
            <form onSubmit={handleSubmit}>
              {!isLoginView && (
                <div className={styles.inputGroup}>
                  <i className={`fas fa-signature ${styles.inputIcon}`}></i>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className={styles.inputGroup}>
                <i className={`fas fa-envelope ${styles.inputIcon}`}></i>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <i className={`fas fa-lock ${styles.inputIcon}`}></i>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                className={styles.loginBtn}
                disabled={isProcessing}
              >
                {isProcessing
                  ? "Processing..."
                  : isLoginView
                  ? "Login Now"
                  : "Create Account"}
              </button>
            </form>

            {isLoginView && (
              <div className={styles.forgotPassword}>
                <button onClick={handleForgotPassword} disabled={isProcessing}>
                  Forgot Password?
                </button>
              </div>
            )}
            <p className={styles.socialLoginLabel}>OR</p>
            <div className={styles.socialLoginContainer}>
              {clientId && !isProcessing && (
                <GoogleOAuthProvider clientId={clientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    theme="outline"
                    shape="pill"
                  />
                </GoogleOAuthProvider>
              )}
            </div>
            {error && <p className={styles.errorMessage}>{error}</p>}
            <div className={styles.toggleView}>
              <span>
                {isLoginView
                  ? "Don't have an account?"
                  : "Already have an account?"}
              </span>
              <button onClick={toggleView}>
                {isLoginView ? "Sign Up" : "Login"}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.imageSide}>
          <div className={styles.promoTextContainer}>
            <div className={styles.sparkIcon}>⚡️</div>
            <h2>Very good works are waiting for you</h2>
            <p>Login Now!!!</p>
          </div>
          <img
            src={promoImage}
            alt="Woman working on a tablet"
            className={styles.promoImage}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
